import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { isUserPermitted } from 'app/cross-cutting-concerns/authentication/utils/isUserPermitted';
import { selectPermissions } from 'app/cross-cutting-concerns/authentication/state/authenticationSelectors';
import { SiteModalsActions } from 'app/modules/site-management/modals/state/siteModalsActions';
import * as siteModalsSelectors from 'app/modules/site-management/modals/state/siteModalsSelectors';
import { Permission } from 'config/permissions';

export const ADD_WORK_INTERVAL_MODAL_QUERY_PARAM = 'addWorkInterval';
export const ADD_WORK_INTERVAL_MACHINE_ID_QUERY_PARAM = 'machineId';
export const SITE_DETAILS_CLEANING_PLAN_SECTION_ID = 'site-details-cleaning-plan';

const SCROLL_TO_CLEANING_PLAN_TIMEOUT_MS = 10000;

const scrollToCleaningPlanSection = (): boolean => {
  const cleaningPlanSection = document.getElementById(SITE_DETAILS_CLEANING_PLAN_SECTION_ID);
  if (!cleaningPlanSection) {
    return false;
  }

  cleaningPlanSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
};

/**
 * Opens the add work interval modal when the URL contains `?addWorkInterval=true`.
 * Keeps `addWorkInterval` and `machineId` while the modal is open and removes them when it closes.
 * Also scrolls the site details drawer/page to the Cleaning Plan section.
 */
export const useOpenAddWorkIntervalModalFromUrl = (): void => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const userPermissions = useSelector(selectPermissions);
  const isVisible = useSelector(siteModalsSelectors.selectIsAddWorkIntervalModalVisible);
  const shouldOpenModal = searchParams.get(ADD_WORK_INTERVAL_MODAL_QUERY_PARAM) === 'true';
  const wasVisibleRef = useRef(false);
  const hasScrolledToCleaningPlanRef = useRef(false);
  const canAddWorkInterval = isUserPermitted(userPermissions, [Permission.Site.WorkInterval.UPDATE]);

  useEffect(() => {
    if (!shouldOpenModal) {
      wasVisibleRef.current = isVisible;
      hasScrolledToCleaningPlanRef.current = false;
      return;
    }

    const clearQueryParams = (): void => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete(ADD_WORK_INTERVAL_MODAL_QUERY_PARAM);
      nextParams.delete(ADD_WORK_INTERVAL_MACHINE_ID_QUERY_PARAM);
      setSearchParams(nextParams, { replace: true });
    };

    if (!canAddWorkInterval) {
      clearQueryParams();
      return;
    }

    const wasVisible = wasVisibleRef.current;

    if (wasVisible && !isVisible) {
      clearQueryParams();
      wasVisibleRef.current = false;
      return;
    }

    if (!isVisible && !wasVisible) {
      dispatch(SiteModalsActions.showAddWorkIntervalModal());
    }

    wasVisibleRef.current = isVisible;
  }, [canAddWorkInterval, dispatch, isVisible, searchParams, setSearchParams, shouldOpenModal]);

  useEffect(() => {
    if (!shouldOpenModal || !canAddWorkInterval || hasScrolledToCleaningPlanRef.current) {
      return undefined;
    }

    if (scrollToCleaningPlanSection()) {
      hasScrolledToCleaningPlanRef.current = true;
      return undefined;
    }

    const observer = new MutationObserver(() => {
      if (scrollToCleaningPlanSection()) {
        hasScrolledToCleaningPlanRef.current = true;
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = window.setTimeout(() => {
      observer.disconnect();
    }, SCROLL_TO_CLEANING_PLAN_TIMEOUT_MS);

    return (): void => {
      observer.disconnect();
      window.clearTimeout(timeoutId);
    };
  }, [canAddWorkInterval, shouldOpenModal]);
};
